import React from "react";
import { Plus, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CustomerList } from "@/components/customers/CustomerList";
import CustomersTable from "@/components/customers/CustomersTable";
import { FilterBuilder } from "@/components/invoices/FilterBuilder";
import { useCustomerFilterBuilder } from "../hooks/useCustomerFilterBuilder";
import { useViewPreference } from "@/hooks/useViewPreference";
import { sortOptions } from "../types";
import { NavigateFunction } from "react-router-dom";
import { Customer } from "@/lib/types";
import PageLayout from "@/components/layout/PageLayout";
import PageHeader from "@/components/layout/PageHeader";
import DataTableWrapper from "@/components/shared/DataTableWrapper";
import ViewToggle from "@/components/shared/ViewToggle";
import FilterToolbar from "@/components/shared/FilterToolbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface CustomerListViewProps {
  loading: boolean;
  customers: Customer[];
  search: string;
  setSearch: (search: string) => void;
  sortBy: string;
  setSortBy: (sortBy: string) => void;
  sortDir: "asc" | "desc";
  setSortDir: (sortDir: "asc" | "desc") => void;
  setOpen: (open: boolean) => void;
  setEditing: (customer: Customer | null) => void;
  viewCustomerDetails: (customer: Customer) => void;
  handleDeleteCustomer: (customerId: string) => void;
  navigate: NavigateFunction;
}

export const CustomerListView: React.FC<CustomerListViewProps> = ({
  loading,
  customers,
  search,
  setSearch,
  sortBy,
  setSortBy,
  sortDir,
  setSortDir,
  setOpen,
  setEditing,
  viewCustomerDetails,
  handleDeleteCustomer,
  navigate
}) => {
  // Use the new filter builder hook
  const {
    filteredCustomers,
    hasActiveFilters,
    draftGroups,
    appliedGroups,
    filterFields,
    filterEntities,
    filterAttributesByEntity,
    addGroup,
    updateGroup,
    removeGroup,
    addRuleToGroup,
    updateRuleInGroup,
    removeRuleFromGroup,
    clearAllRules,
    applyFilters,
    hasUnappliedChanges,
    canApplyFilters
  } = useCustomerFilterBuilder(customers);

  const { viewMode, setViewMode } = useViewPreference({ entityType: 'customers', defaultView: 'grid' });

  // Apply search filter to the filtered customers
  const searchFilteredCustomers = React.useMemo(() => {
    if (!search.trim()) return filteredCustomers;
    
    const searchLower = search.toLowerCase();
    return filteredCustomers.filter(customer => {
      const searchableFields = [
        customer.name,
        customer.last_name,
        customer.emails?.join(' '),
        customer.cell_numbers?.join(' '),
        customer.street_address,
        customer.suburb,
        customer.city,
        customer.province_state,
        customer.postal_code,
        customer.lead_source,
        customer.tags?.join(' ')
      ];
      
      return searchableFields.some(field => 
        field?.toLowerCase().includes(searchLower)
      );
    });
  }, [filteredCustomers, search]);

  // Apply sorting to the search filtered customers
  const finalCustomers = React.useMemo(() => {
    const sorted = [...searchFilteredCustomers].sort((a, b) => {
      let aVal: any, bVal: any;

      switch (sortBy) {
        case "name":
          aVal = `${a.name} ${a.last_name}`.toLowerCase();
          bVal = `${b.name} ${b.last_name}`.toLowerCase();
          break;
        case "created_at":
          aVal = new Date(a.created_at);
          bVal = new Date(b.created_at);
          break;
        case "updated_at":
          aVal = new Date(a.updated_at);
          bVal = new Date(b.updated_at);
          break;
        case "lead_source":
          aVal = (a.lead_source || "").toLowerCase();
          bVal = (b.lead_source || "").toLowerCase();
          break;
        case "city":
          aVal = (a.city || "").toLowerCase();
          bVal = (b.city || "").toLowerCase();
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [searchFilteredCustomers, sortBy, sortDir]);

  return (
    <PageLayout>
      <PageHeader 
        title="Customers"
        description="Manage your customer database"
      />
      
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              All Customers
            </CardTitle>
            <ViewToggle value={viewMode} onChange={setViewMode} />
          </div>
          {/* Unified FilterToolbar */}
          <FilterToolbar
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search customers..."
            sortOptions={sortOptions.map(opt => ({ value: opt.value, label: opt.label }))}
            sortValue={sortBy}
            onSortChange={setSortBy}
            sortDir={sortDir}
            onSortDirChange={setSortDir}
            actions={
              <Button onClick={() => navigate('/customers/new')} size="sm" className="shrink-0 px-4">
                <Plus className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">New Customer</span>
                <span className="sm:hidden">Add</span>
              </Button>
            }
            advancedFilter={
              <FilterBuilder
                draftGroups={draftGroups}
                appliedGroups={appliedGroups}
                fields={filterFields}
                entities={filterEntities}
                attributesByEntity={filterAttributesByEntity}
                onAddGroup={addGroup}
                onUpdateGroup={updateGroup}
                onRemoveGroup={removeGroup}
                onAddRuleToGroup={addRuleToGroup}
                onUpdateRuleInGroup={updateRuleInGroup}
                onRemoveRuleFromGroup={removeRuleFromGroup}
                onClearAll={clearAllRules}
                onApplyFilters={applyFilters}
                hasActiveFilters={hasActiveFilters}
                hasUnappliedChanges={hasUnappliedChanges}
                canApplyFilters={canApplyFilters}
              />
            }
            hasAdvancedFilters={hasActiveFilters}
            advancedFilterCount={appliedGroups.reduce((c, g) => c + g.rules.filter(r => r.entity && r.attribute && r.value).length, 0)}
            onClearAdvancedFilters={clearAllRules}
          />
        </CardHeader>
        <CardContent className={viewMode === 'list' ? 'p-0' : undefined}>
          <DataTableWrapper
            isLoading={loading}
            isEmpty={finalCustomers.length === 0}
            emptyState={{
              title: hasActiveFilters || search ? "No customers match your filters" : "No customers found",
              description: hasActiveFilters || search 
                ? "Try adjusting your search or filters." 
                : "Get started by adding your first customer.",
              icon: <Users className="h-12 w-12 text-muted-foreground" />,
              action: hasActiveFilters || search ? (
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setSearch("");
                    clearAllRules();
                  }}
                  className="mt-4"
                >
                  Clear filters
                </Button>
              ) : (
                <Button 
                  onClick={() => navigate('/customers/new')}
                  className="mt-4"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Customer
                </Button>
              )
            }}
          >
            {viewMode === 'list' ? (
              <CustomersTable
                customers={finalCustomers}
                viewCustomerDetails={viewCustomerDetails}
                setEditing={setEditing}
                setOpen={setOpen}
                handleDeleteCustomer={handleDeleteCustomer}
              />
            ) : (
              <CustomerList
                customers={finalCustomers}
                viewCustomerDetails={viewCustomerDetails}
                setEditing={setEditing}
                setOpen={setOpen}
                handleDeleteCustomer={handleDeleteCustomer}
              />
            )}
          </DataTableWrapper>
        </CardContent>
      </Card>
    </PageLayout>
  );
};
